import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import Footer from '../components/Footer';
import { Info, Shield, Database, Eye, Lock, User } from "lucide-react";

const PRIVACY_SECTIONS = [
  {
    id: "collection",
    icon: <Database />,
    title: "Information We Collect",
    content: [
      "Personal details such as name, phone number, and email address",
      "Shipping and billing address",
      "Payment details (processed securely via third-party providers)",
      "Device and browser information",
    ],
  },
  {
    id: "usage",
    icon: <Eye />,
    title: "How We Use Information",
    content: [
      "To process and deliver shipments",
      "To improve our platform and services",
      "To communicate updates, offers, and notifications",
      "To ensure compliance with legal requirements",
    ],
  },
  {
    id: "sharing",
    icon: <User />,
    title: "Information Sharing",
    content: [
      "We do not sell your personal data",
      "Data may be shared with logistics partners for delivery",
      "Shared with payment gateways for secure transactions",
      "Shared when required by law or authorities",
    ],
  },
  {
    id: "security",
    icon: <Lock />,
    title: "Data Security",
    content: [
      "We use encryption and secure servers",
      "Access to data is restricted to authorized personnel",
      "Regular audits and monitoring for vulnerabilities",
    ],
  },
  {
    id: "rights",
    icon: <Shield />,
    title: "User Rights",
    content: [
      "You can request access to your data",
      "You can request correction or deletion",
      "You can opt out of marketing communications",
    ],
  },
];

function PrivacySection({ section, isOpen, onClick }) {
  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm dark:bg-slate-900 dark:border-slate-700">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-800"
      >
        <div className="flex items-center gap-3">
          <div className="text-slate-600 dark:text-slate-300">
            {React.cloneElement(section.icon, { size: 20 })}
          </div>
          <span className="font-medium text-slate-700 dark:text-slate-200">
            {section.title}
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="p-4 border-t bg-slate-50/50 dark:bg-slate-800/50 dark:border-slate-700">
          <ul className="list-disc ml-5 text-sm text-gray-600 dark:text-slate-300 space-y-1">
            {section.content.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const PrivacyPolicyPage = () => {
  const [openId, setOpenId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <PublicNavbar />
      
      <div className="pt-32 pb-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-5xl font-black mb-8 text-[#1a2b4b] tracking-tight">
          Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Policy</span>
        </h2>

        {/* Info Banner */}
        <div className="flex items-center gap-4 bg-indigo-50 p-6 rounded-2xl mb-10 border border-indigo-100">
          <div className="bg-indigo-100 p-2 rounded-full">
            <Info className="w-5 h-5 text-indigo-600" />
          </div>
          <p className="text-indigo-900 text-sm font-bold">
            Your privacy is important to us. This policy explains how we collect,
            use, and protect your information.
          </p>
        </div>

        {/* Sections */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-slate-200 space-y-4">
          {PRIVACY_SECTIONS.map((section) => (
            <PrivacySection
              key={section.id}
              section={section}
              isOpen={openId === section.id}
              onClick={() =>
                setOpenId(openId === section.id ? null : section.id)
              }
            />
          ))}

          {/* Footer Note */}
          <div className="mt-12 pt-8 border-t border-slate-100 text-slate-500 text-sm leading-relaxed">
            <p>
              By using our platform, you agree to the terms outlined in this Privacy
              Policy. We may update this policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;